import type {
  HourlyBonusPolicyCreateApiData,
  HourlyBonusPolicyCreateApiResponses,
  HourlyBonusPolicyDeleteApiData,
  HourlyBonusPolicyDeleteApiResponses,
  HourlyBonusPolicyListApiData,
  HourlyBonusPolicyListApiResponses,
  HourlyBonusPolicyUpdateApiData,
  HourlyBonusPolicyUpdateApiResponses,
  HourlyBonusPreviewApiData,
  HourlyBonusPreviewApiResponses,
  PayrollBasisExportApiData,
  PayrollBasisExportApiResponses,
  PayrollCreateApiData,
  PayrollCreateApiResponses,
  PayrollDeleteApiData,
  PayrollDeleteApiResponses,
  PayrollExportApiData,
  PayrollExportApiResponses,
  PayrollGetApiData,
  PayrollGetApiResponses,
  PayrollGetNextStartDateApiData,
  PayrollGetNextStartDateApiResponses,
  PayrollHourlyBonusSettlementsGetApiData,
  PayrollHourlyBonusSettlementsGetApiResponses,
  PayrollListApiData,
  PayrollListApiResponses,
  PayrollListForServiceDepartmentApiData,
  PayrollListForServiceDepartmentApiResponses,
  PayrollMarkAsApprovedApiData,
  PayrollMarkAsApprovedApiResponses,
  PayrollMarkAsExportedApiData,
  PayrollMarkAsExportedApiResponses,
  PayrollRouteSettlementsGetApiData,
  PayrollRouteSettlementsGetApiResponses,
  PayrollServiceWorkersSummaryGetApiData,
  PayrollServiceWorkersSummaryGetApiResponses,
  PayrollSetToDraftApiData,
  PayrollSetToDraftApiResponses,
  PayrollSummaryGetApiData,
  PayrollSummaryGetApiResponses,
  PayrollTimeRefreshApiData,
  PayrollTimeRefreshApiResponses,
  PayrollTimeSettlementsGetApiData,
  PayrollTimeSettlementsGetApiResponses,
  PayrollTimeSettlementsSummaryGetApiData,
  PayrollTimeSettlementsSummaryGetApiResponses,
  RouteSettlementListApiData,
  RouteSettlementListApiResponses,
  RouteSettlementListForServiceDepartmentApiData,
  RouteSettlementListForServiceDepartmentApiResponses,
  RouteSettlementStatsApiData,
  RouteSettlementStatsApiResponses
} from '@navio/nsdk';

import { downloadToExcel } from '../../downloadToExcel';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';

export const getPayrollUrls = {
  getAllPaginatedPayrolls: dataEndpoint<PayrollListApiData, PayrollListApiResponses>({
    getUrl: (queryParams) => apiUrl('payrolls/', queryParams)
  }),
  getPaginatedPayrolls: dataEndpoint<
    PayrollListForServiceDepartmentApiData,
    PayrollListForServiceDepartmentApiResponses
  >({
    getUrl: ({ serviceDepartmentId, ...queryParams }) =>
      apiUrl(`service-departments/${serviceDepartmentId}/payrolls/`, queryParams)
  }),
  getPayroll: dataEndpoint<PayrollGetApiData, PayrollGetApiResponses>({
    getUrl: ({ payrollId }) => apiUrl(`payrolls/${payrollId}/`)
  }),
  getPayrollServiceWorkersSummary: dataEndpoint<
    PayrollServiceWorkersSummaryGetApiData,
    PayrollServiceWorkersSummaryGetApiResponses
  >({
    getUrl: ({ payrollId }) => apiUrl(`payrolls/${payrollId}/service-workers-summary/`)
  }),
  getPayrollTimeSettlementsSummary: dataEndpoint<
    PayrollTimeSettlementsSummaryGetApiData,
    PayrollTimeSettlementsSummaryGetApiResponses
  >({
    getUrl: ({ payrollId }) => apiUrl(`payrolls/${payrollId}/time-settlements-summary/`)
  }),
  getPayrollRouteSettlements: dataEndpoint<PayrollRouteSettlementsGetApiData, PayrollRouteSettlementsGetApiResponses>({
    getUrl: ({ payrollId }) => apiUrl(`payrolls/${payrollId}/route-settlements/`)
  }),
  getPayrollTimeSettlements: dataEndpoint<PayrollTimeSettlementsGetApiData, PayrollTimeSettlementsGetApiResponses>({
    getUrl: ({ payrollId }) => apiUrl(`payrolls/${payrollId}/time-settlements/`)
  }),
  getPayrollHourlyBonusSettlements: dataEndpoint<
    PayrollHourlyBonusSettlementsGetApiData,
    PayrollHourlyBonusSettlementsGetApiResponses
  >({
    getUrl: ({ payrollId }) => apiUrl(`payrolls/${payrollId}/hourly-bonus-settlements/`)
  }),
  getStartDateForNewPayroll: dataEndpoint<PayrollGetNextStartDateApiData, PayrollGetNextStartDateApiResponses>({
    getUrl: ({ serviceDepartmentId, ...queryParams }) =>
      apiUrl(`service-departments/${serviceDepartmentId}/payrolls/next-start-date/`, queryParams)
  }),
  getSummaryForNewPayroll: dataEndpoint<PayrollSummaryGetApiData, PayrollSummaryGetApiResponses>({
    getUrl: ({ serviceDepartmentId, ...queryParams }) =>
      apiUrl(`service-departments/${serviceDepartmentId}/payrolls/summary/`, queryParams)
  }),
  postCreatePayroll: responseEndpoint<PayrollCreateApiData, PayrollCreateApiResponses>({
    getUrl: ({ serviceDepartmentId }) => apiUrl(`service-departments/${serviceDepartmentId}/payrolls/`),
    getBody: (data) => data
  }),
  deletePayroll: dataEndpoint<PayrollDeleteApiData, PayrollDeleteApiResponses>({
    getUrl: ({ payrollId }) => apiUrl(`payrolls/${payrollId}/`)
  }),
  getExportPayroll: dataEndpoint<PayrollExportApiData, PayrollExportApiResponses>({
    getUrl: ({ payrollId, ...queryParams }) => apiUrl(`payrolls/${payrollId}/export/`, queryParams),
    handleRes: async (res) => downloadToExcel(res),
    responseType: 'blob',
    sideEffectGet: true
  }),
  getExportPayrollBasis: dataEndpoint<PayrollBasisExportApiData, PayrollBasisExportApiResponses>({
    getUrl: ({ payrollId, ...queryParams }) => apiUrl(`payrolls/${payrollId}/export/payroll-basis/`, queryParams),
    handleRes: async (res) => downloadToExcel(res),
    responseType: 'blob',
    sideEffectGet: true
  }),
  getHourlyBonusPolicies: dataEndpoint<HourlyBonusPolicyListApiData, HourlyBonusPolicyListApiResponses>({
    getUrl: (queryParams) => apiUrl('hourly-bonus-policies/', queryParams)
  }),
  postHourlyBonusPolicy: responseEndpoint<HourlyBonusPolicyCreateApiData, HourlyBonusPolicyCreateApiResponses>({
    getUrl: () => apiUrl('hourly-bonus-policies/'),
    getBody: (data) => data
  }),
  patchHourlyBonusPolicy: responseEndpoint<HourlyBonusPolicyUpdateApiData, HourlyBonusPolicyUpdateApiResponses>({
    getUrl: ({ hourlyBonusPolicyId }) => apiUrl(`hourly-bonus-policies/${hourlyBonusPolicyId}/`),
    getBody: ({ hourlyBonusPolicyId: _hourlyBonusPolicyId, ...body }) => body
  }),
  postHourlyBonusPreview: responseEndpoint<HourlyBonusPreviewApiData, HourlyBonusPreviewApiResponses>({
    getUrl: () => apiUrl('hourly-bonus-policies/preview/'),
    getBody: (data) => data
  }),
  deleteHourlyBonusPolicy: dataEndpoint<HourlyBonusPolicyDeleteApiData, HourlyBonusPolicyDeleteApiResponses>({
    getUrl: ({ hourlyBonusPolicyId }) => apiUrl(`hourly-bonus-policies/${hourlyBonusPolicyId}/`)
  }),
  patchRefreshPayrollTime: responseEndpoint<PayrollTimeRefreshApiData, PayrollTimeRefreshApiResponses>({
    getUrl: ({ payrollId }) => apiUrl(`payrolls/${payrollId}/refresh-time/`),
    getBody: (data) => data
  }),
  patchMarkPayrollAsApproved: responseEndpoint<PayrollMarkAsApprovedApiData, PayrollMarkAsApprovedApiResponses>({
    getUrl: ({ payrollId }) => apiUrl(`payrolls/${payrollId}/mark-as-approved/`),
    getBody: (data) => data
  }),
  patchMarkPayrollAsExported: responseEndpoint<PayrollMarkAsExportedApiData, PayrollMarkAsExportedApiResponses>({
    getUrl: ({ payrollId }) => apiUrl(`payrolls/${payrollId}/mark-as-exported/`),
    getBody: (data) => data
  }),
  patchSetPayrollToDraft: responseEndpoint<PayrollSetToDraftApiData, PayrollSetToDraftApiResponses>({
    getUrl: ({ payrollId }) => apiUrl(`payrolls/${payrollId}/set-to-draft/`),
    getBody: (data) => data
  }),
  getPaginatedServiceDepartmentRouteSettlements: dataEndpoint<
    RouteSettlementListForServiceDepartmentApiData,
    RouteSettlementListForServiceDepartmentApiResponses
  >({
    getUrl: ({ serviceDepartmentId, ...queryParams }) =>
      apiUrl(`service-departments/${serviceDepartmentId}/route-settlements/`, queryParams)
  }),
  getPaginatedRouteSettlements: dataEndpoint<RouteSettlementListApiData, RouteSettlementListApiResponses>({
    getUrl: (queryParams) => apiUrl('route-settlements/', queryParams)
  }),
  getRouteSettlementStats: dataEndpoint<RouteSettlementStatsApiData, RouteSettlementStatsApiResponses>({
    getUrl: (queryParams) => apiUrl('route-settlements/stats/', queryParams)
  })
};
